import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { motion, useTransform } from 'motion/react';
import { deleteStelle, updateStelle } from '../api';
import { AnimateIcon } from './animate-ui/icons/AnimateIcon';
import { Trash2 } from './animate-ui/icons/Trash2';
import './Papierkorb.css';

const TTL_TAGE = 7;
const PREFIX = 'papierkorb';

// Status-String kodiert: papierkorb#<urspruenglicherStatus>#<isoDatum>
// (Backend ist Team-Code; ein eigenes Lösch-Datum-Feld wäre ein Patch -> daher in den Status packen.)
const istPapierkorb = (status) => (status || '').startsWith(PREFIX);
const packPapierkorb = (origStatus, isoDatum) => `${PREFIX}#${origStatus || ''}#${isoDatum}`;
const parsePapierkorb = (status) => {
  const [, origStatus = '', datum = ''] = (status || '').split('#');
  return { origStatus, datum };
};

const tageHer = (isoDatum) => {
  if (!isoDatum) return 0;
  return Math.floor((Date.now() - new Date(isoDatum).getTime()) / 86400000);
};

export default function Papierkorb({ stellen, setStellen, panelWidth }) {
  const [offen, setOffen] = useState(false);       // Modal sichtbar
  const [ueberZone, setUeberZone] = useState(false); // wird gerade über die Tonne gezogen
  const [animLauf, setAnimLauf] = useState(false);   // Deckel-Animation nach dem Ablegen
  const [undo, setUndo] = useState(null);          // { id }

  // horizontal mittig unter der rechten Sidebar: 12px Studio-Padding + halbe Panelbreite,
  // minus halbe Buttonbreite (~57px bei Symbolgröße 102). Folgt der Panelbreite live beim Resizen.
  const rechts = useTransform(panelWidth, (w) => 12 + (w || 0) / 2 - 57);

  const papierkorbStellen = stellen.filter((s) => istPapierkorb(s.bewerbungs_status));

  const setzeStatus = useCallback((id, status) => {
    updateStelle(id, { bewerbungs_status: status }).catch(() => {});
    setStellen((prev) => prev.map((s) => (s.id === id ? { ...s, bewerbungs_status: status } : s)));
  }, [setStellen]);

  // ponytail: Purge läuft client-seitig beim App-Start; öffnet niemand die App, bleibt der
  // Eintrag liegen — für ein persönliches Tool ok, später Backend-Cron falls nötig.
  useEffect(() => {
    const alt = stellen.filter(
      (s) => istPapierkorb(s.bewerbungs_status) && tageHer(parsePapierkorb(s.bewerbungs_status).datum) >= TTL_TAGE
    );
    if (!alt.length) return;
    alt.forEach((s) => deleteStelle(s.id).catch(() => {}));
    setStellen((prev) => prev.filter((s) => !alt.some((a) => a.id === s.id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const inPapierkorb = useCallback((id) => {
    const stelle = stellen.find((s) => s.id === id);
    if (!stelle || istPapierkorb(stelle.bewerbungs_status)) return;
    setzeStatus(id, packPapierkorb(stelle.bewerbungs_status || '', new Date().toISOString()));
    setUndo({ id });
  }, [stellen, setzeStatus]);

  const wiederherstellen = useCallback((id) => {
    const stelle = stellen.find((s) => s.id === id);
    setzeStatus(id, stelle ? parsePapierkorb(stelle.bewerbungs_status).origStatus : '');
  }, [stellen, setzeStatus]);

  const papierkorbLeeren = useCallback(async () => {
    if (!papierkorbStellen.length) return;
    await Promise.all(papierkorbStellen.map((s) => deleteStelle(s.id).catch(() => {})));
    setStellen((prev) => prev.filter((s) => !istPapierkorb(s.bewerbungs_status)));
  }, [papierkorbStellen, setStellen]);

  // Undo-Leiste nach 3 s ausblenden (Eintrag bleibt im Papierkorb)
  useEffect(() => {
    if (!undo) return;
    const t = setTimeout(() => setUndo(null), 3000);
    return () => clearTimeout(t);
  }, [undo]);

  const onDrop = (e) => {
    e.preventDefault();
    setUeberZone(false);
    const id = e.dataTransfer.getData('text/plain');
    if (id) {
      inPapierkorb(id);
      setAnimLauf(true);                       // Deckel erst jetzt zuklappen
      setTimeout(() => setAnimLauf(false), 500);
    }
  };

  return (
    <>
      <motion.button
        className={`papierkorb-fab${ueberZone ? ' ueber' : ''}`}
        style={{ right: rechts }}
        title="Papierkorb – Bewerbungen hierher ziehen"
        onClick={() => setOffen(true)}
        onDragOver={(e) => { e.preventDefault(); setUeberZone(true); }}
        onDragLeave={() => setUeberZone(false)}
        onDrop={onDrop}
      >
        <AnimateIcon animate={animLauf ? 'trash' : false}>
          <Trash2 size={102} />
        </AnimateIcon>
        {papierkorbStellen.length > 0 && (
          <span className="papierkorb-badge">{papierkorbStellen.length}</span>
        )}
      </motion.button>

      {undo && ReactDOM.createPortal(
        <div className="papierkorb-undo">
          <span>Bewerbung in den Papierkorb verschoben.</span>
          <button onClick={() => { wiederherstellen(undo.id); setUndo(null); }}>Rückgängig</button>
          <span className="papierkorb-undo__bar" />
        </div>,
        document.body
      )}

      {offen && ReactDOM.createPortal(
        <div className="papierkorb-overlay" onClick={() => setOffen(false)}>
          <div className="papierkorb-insel" onClick={(e) => e.stopPropagation()}>
            <button className="papierkorb-insel__close" onClick={() => setOffen(false)}>✕</button>
            <h3>Papierkorb</h3>
            <p className="papierkorb-hinweis">
              Einträge werden nach {TTL_TAGE} Tagen automatisch endgültig gelöscht.
            </p>
            {papierkorbStellen.length === 0 ? (
              <p className="papierkorb-leer">Der Papierkorb ist leer.</p>
            ) : (
              <>
                <ul className="papierkorb-liste">
                  {papierkorbStellen.map((s) => {
                    const tage = tageHer(parsePapierkorb(s.bewerbungs_status).datum);
                    return (
                      <li key={s.id}>
                        <div className="papierkorb-liste__info">
                          <strong>{s.name}</strong>
                          <span>{tage === 0 ? 'heute gelöscht' : `vor ${tage} Tag${tage === 1 ? '' : 'en'} gelöscht`}</span>
                        </div>
                        <button className="papierkorb-wieder" onClick={() => wiederherstellen(s.id)}>
                          Wiederherstellen
                        </button>
                      </li>
                    );
                  })}
                </ul>
                <button className="papierkorb-leeren-btn" onClick={papierkorbLeeren}>
                  Papierkorb leeren
                </button>
              </>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

export { istPapierkorb };
